import { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Loader2, AlertTriangle, ScanLine, WifiOff } from 'lucide-react';
import { api, type AperturasSalud } from '@/lib/api';
import { madridDay, Panel, Stat } from '@/components/aperturas/shared';
import type { AperturasContext } from './AperturasLayout';

/** Nuki completion states. 0 is success and 224 is a rejected code, a people
 *  problem rather than a hardware one, so neither shows up here. */
const STATE_LABELS: Record<string, string> = {
  1: 'Motor bloqueado',
  2: 'Cancelado',
  3: 'Demasiado reciente',
  4: 'Ocupado',
  5: 'Bajo voltaje del motor',
  6: 'Fallo del embrague',
  7: 'Fallo de potencia del motor',
  8: 'Incompleto',
  9: 'Rechazado',
  10: 'Modo noche',
  254: 'Otro error',
  255: 'Error desconocido',
};

const ACTION_LABELS: Record<string, string> = {
  sensor_atascado: 'Sensor de puerta atascado',
  puerta_entreabierta: 'Puerta entreabierta',
};

/**
 * Cada dispositivo enlaza a su ficha del inventario. El id de la ficha es
 * `nuki-<smartlockId>` por construcción, así que no hace falta consultar
 * nada; si aún no se ha sincronizado, la ficha lo dice y ofrece hacerlo.
 */
function Dispositivo({ smartlockId, label }: { smartlockId: number; label: string }) {
  return (
    <Link to={`/dispositivos/nuki-${smartlockId}`} className="hover:underline" title="Abrir la ficha en Dispositivos">
      {label}
    </Link>
  );
}

function diasLabel(d: number): string {
  if (d === 0) return 'hoy';
  if (d === 1) return 'ayer';
  return `hace ${d} días`;
}

export default function AperturasSaludPage() {
  const { days } = useOutletContext<AperturasContext>();
  const [data, setData] = useState<AperturasSalud | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.getAperturasSalud(madridDay(days - 1), madridDay())
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Error cargando datos'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [days]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500 -mt-1">Dispositivos callados, fallos mecánicos y cobertura de sensores, derivado del mismo flujo de eventos.</p>

      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="animate-spin mr-2" size={20} /> Cargando…
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Dispositivos vistos" value={data.dispositivos.length} />
            <Stat label="Sin eventos ≥ 7 días" value={data.silenciosos.length} />
            <Stat label="Fallos de accionamiento" value={data.fallos.reduce((s, f) => s + f.total, 0)} />
            <Stat
              label="Con sensor de puerta"
              value={`${data.sensor.conSensor} / ${data.sensor.totalDispositivos}`}
              hint="el resto no sabe si la puerta se abrió"
            />
          </div>

          {data.silenciosos.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 p-4">
              <div className="flex items-center gap-2 font-semibold text-amber-800 dark:text-amber-300 text-sm">
                <WifiOff size={16} />
                {data.silenciosos.length === 1
                  ? '1 dispositivo sin eventos desde hace una semana o más'
                  : `${data.silenciosos.length} dispositivos sin eventos desde hace una semana o más`}
              </div>
              <p className="text-xs text-amber-800/70 dark:text-amber-300/70 mt-1">
                Puede ser un apartamento vacío, pero también una cerradura desconectada o sin batería.
              </p>
              <ul className="mt-2 space-y-1 text-sm text-amber-900 dark:text-amber-200">
                {data.silenciosos.map((d) => (
                  <li key={d.smartlockId}>
                    <span className="font-medium"><Dispositivo smartlockId={d.smartlockId} label={d.deviceLabel} /></span> — último evento {diasLabel(d.diasSinEventos)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Panel
            title="Fallos de accionamiento"
            hint="Estado distinto de 0 en la respuesta de la cerradura. Excluye los códigos rechazados, que son un problema de personas y no de hardware."
          >
            {data.fallos.length === 0 ? (
              <p className="text-sm text-slate-400">Ningún fallo registrado en el rango.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-slate-500">
                  <tr>
                    <th className="text-left font-medium pb-2">Dispositivo</th>
                    <th className="text-left font-medium pb-2">Estados</th>
                    <th className="text-right font-medium pb-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.fallos.map((f) => (
                    <tr key={f.smartlockId} className="border-t border-slate-100 dark:border-white/5">
                      <td className="py-2 text-sereno-dark"><Dispositivo smartlockId={f.smartlockId} label={f.deviceLabel} /></td>
                      <td className="py-2 text-slate-500 text-xs">
                        {Object.entries(f.estados)
                          .map(([s, n]) => `${STATE_LABELS[s] ?? `Estado ${s}`} ×${n}`)
                          .join(' · ')}
                      </td>
                      <td className="py-2 text-right tabular-nums font-medium">{f.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          <Panel title="Alertas del sensor de puerta">
            {data.alertas.length === 0 ? (
              <p className="text-sm text-slate-400">Sin alertas en el rango.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {data.alertas.map((a) => (
                  <li key={`${a.smartlockId}-${a.actionLabel}`} className="flex items-center gap-2">
                    <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                    <span className="text-sereno-dark"><Dispositivo smartlockId={a.smartlockId} label={a.deviceLabel} /></span>
                    <span className="text-slate-500">{ACTION_LABELS[a.actionLabel] ?? a.actionLabel}</span>
                    <span className="text-slate-400 tabular-nums">×{a.n}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel
            title="Cobertura de sensor de puerta"
            hint="Sin sensor solo se sabe que giró el motor. Ampliar esta cobertura es lo que convertiría el reloj de aperturas en un reloj de puertas de verdad."
          >
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
              <ScanLine size={15} className="text-slate-400" />
              {data.sensor.conSensor} de {data.sensor.totalDispositivos} dispositivos reportan estado de puerta
            </div>
            <ul className="space-y-1 text-sm">
              {data.sensor.dispositivos.map((d) => (
                <li key={d.smartlockId} className="flex justify-between">
                  <span className="text-sereno-dark"><Dispositivo smartlockId={d.smartlockId} label={d.deviceLabel} /></span>
                  <span className="tabular-nums text-slate-500">{d.n.toLocaleString('es-ES')} lecturas</span>
                </li>
              ))}
            </ul>
          </Panel>

          <Panel title="Actividad por dispositivo">
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-slate-500 sticky top-0 bg-white dark:bg-sereno-dark">
                  <tr>
                    <th className="text-left font-medium pb-2">Dispositivo</th>
                    <th className="text-left font-medium pb-2">Tipo</th>
                    <th className="text-right font-medium pb-2">Eventos</th>
                    <th className="text-right font-medium pb-2">Último</th>
                  </tr>
                </thead>
                <tbody>
                  {data.dispositivos.map((d) => (
                    <tr key={d.smartlockId} className="border-t border-slate-100 dark:border-white/5">
                      <td className="py-1.5 text-sereno-dark">
                        <Dispositivo smartlockId={d.smartlockId} label={d.deviceLabel} />
                        {d.tieneSensor && <ScanLine size={12} className="inline ml-1.5 text-slate-400" />}
                      </td>
                      <td className="py-1.5 text-slate-400 text-xs">{d.deviceKind}</td>
                      <td className="py-1.5 text-right tabular-nums text-slate-500">{d.eventosEnRango.toLocaleString('es-ES')}</td>
                      <td className={`py-1.5 text-right text-xs ${d.diasSinEventos >= 7 ? 'text-amber-600 font-medium' : 'text-slate-400'}`}>
                        {diasLabel(d.diasSinEventos)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
